// The emotes, on five dark keys over the tab keys. The row sits in the same
// place on every screen and every tab. Its keys work while the llama has a
// seat in a run: the game plays an emote at any time in a run, between stages
// too. In the queue and at a run's end the keys show but do not press.

import { EMOTES, type Emote } from '../protocol';
import { keycap, setKeyEnabled } from './chrome';
import { el, image } from './dom';
import type { Page } from './page';
import type { Screen } from './state';
import { STRINGS } from './strings';

const EMOTE_COOLDOWN_SHOWN_MS = 1500;

// The screens of a seated llama in a run. At the run's end the game has no
// field for the llama to stand in.
const OPEN_ON: ReadonlySet<Screen['kind']> = new Set([
  'waiting',
  'hold',
  'counting',
  'pad',
  'locked',
  'result',
]);

let emoteReadyAt = 0;

export const emotesOpen = (screen: Screen): boolean => OPEN_ON.has(screen.kind);

export function emoteRow(page: Page, open: boolean): HTMLElement {
  const row = el('div', 'emote-row');
  for (const kind of EMOTES) row.append(emoteKey(page, kind, open));
  return row;
}

// An emote on a dark key: its art, and a hop when it goes out.
function emoteKey(page: Page, kind: Emote, open: boolean): HTMLButtonElement {
  // The art alone: five names do not fit five keys on a phone. The name is the
  // key's label for a screen reader.
  const art = image('emote-art', `/emotes/${kind}.svg`, '');
  const key = keycap({
    label: STRINGS.emotes[kind],
    face: 'dark',
    content: art,
    className: 'key-emote',
    onPress: () => {
      const now = Date.now();
      if (now < emoteReadyAt) return;
      emoteReadyAt = now + EMOTE_COOLDOWN_SHOWN_MS;
      page.send({ t: 'emote', kind });
      page.hologram.playEmote(kind);
      art.classList.remove('is-hopping');
      // Restarts the hop on a quick second tap.
      void art.offsetWidth;
      art.classList.add('is-hopping');
    },
  });
  setKeyEnabled(key, open);
  return key;
}
